import { Box, Typography, Alert } from '@mui/material';
import { useMemo, useCallback, useState } from 'react';
import { Button, GridContainer, GridItem, PositionedMenu, Link, ResyncDetails } from '../../../components/shared';
import ReactTable from '../../../components/shared/ReactTable';
import { COLUMNS } from '../../../constants/tableColumns';
import {
  useGetMachineDetailsByIdQuery,
  useLazyGetAssetResyncStatusQuery,
} from '../../../services/Machines/MachinesService';
import MachineAccordion from '../../../components/MachineAccordion/MachineAccordion';
import useStyles from './style';
import { routeConstants } from '../../../constants/routeConstants';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  useRemoveModemFromMachineMutation,
  useLazyGetModemResyncStatusQuery,
} from '../../../services/Modems/ModemsService';
import { Add } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const AssetDetails = ({ assetId, setAssignNewMachine, modemId, refetchModemDetails }) => {
  const classes = useStyles();
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const {
    isFetching,
    isLoading,
    isSuccess,
    currentData: machineDetails,
    isError,
    refetch,
  } = useGetMachineDetailsByIdQuery(Number(assetId), {
    skip: !assetId || isNaN(Number(assetId)),
  });
  const [removeModemFromMachine] = useRemoveModemFromMachineMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const [getModemResyncStatus] = useLazyGetModemResyncStatusQuery();
  const [resyncWarning, setResyncWarning] = useState('');
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  const titleLink = useCallback(
    (id, name) => (
      <Link underline="none" to={`/${routeConstants.MACHINES_ROUTE}/${id}`}>
        {name}
      </Link>
    ),
    [],
  );
  const COLUMN_POSITIONED_MENU = [
    {
      Header: () => null,
      id: 'more',
      Cell: () => (
        <PositionedMenu
          menuItemData={[
            {
              btnName: 'Remove Machine',
              btnClickFunc: () => {
                onRemoveHandler();
              },
            },
          ]}
        />
      ),
    },
  ];
  const columns = [COLUMNS.EXPAND_ICON_COLUMN, ...COLUMNS.ASSIGN_MACHINE_TABLE, ...COLUMN_POSITIONED_MENU];
  const machineResyncData = useMemo(() => {
    const resync = machineDetails?.data?.resync;

    if (!resync) return [];

    return Array.isArray(resync) ? resync.filter(Boolean) : Object.values(resync).filter(Boolean);
  }, [machineDetails?.data?.resync]);

  const data = useMemo(() => {
    const tableData = [];
    const sublocationName = machineDetails?.data?.sublocation?.name;
    const modelTitle = machineDetails?.data?.model?.title;
    const statusTitle = machineDetails?.data?.status?.title;
    const machineData = {
      ...machineDetails?.data,
      sublocationName,
      modelTitle,
      statusTitle,
    };
    machineData.title = titleLink(machineData.id, machineData.title);
    tableData.push(machineData);
    return tableData;
  }, [machineDetails, isSuccess]);

  const machineComponent = ({ row }) => {
    const original = row?.original;
    return original && <MachineAccordion machineInfo={original} />;
  };

  const assignMachineHandler = () => {
    setAssignNewMachine(true);
  };
  const onRemoveHandler = async () => {
    setResyncWarning('');
    if (isResyncEnabled) {
      // Check modem resync status before removing
      try {
        const modemResyncResult = await getModemResyncStatus(modemId).unwrap();
        if (modemResyncResult?.data?.resync) {
          setResyncWarning(
            'A resync is currently in progress for this modem. Please try again after resync is complete.',
          );
          return;
        }
      } catch {
        // If API fails, proceed
      }
      // Check asset resync status before removing
      try {
        const resyncResult = await getAssetResyncStatus(Number(assetId)).unwrap();
        if (resyncResult?.data?.resync) {
          setResyncWarning(
            'A resync is currently in progress for this asset. Please try again after resync is complete.',
          );
          return;
        }
      } catch {
        // If API fails, proceed
      }
    }
    await removeModemFromMachine({
      id: modemId,
      rest: {
        assetId: null,
        updatedBy,
        modemId: modemId,
      },
    }).unwrap();
    refetchModemDetails();
    refetch();
  };

  return (
    <Box>
      {resyncWarning && (
        <Box sx={{ mx: 3, mb: 2 }}>
          <Alert severity="warning">{resyncWarning}</Alert>
        </Box>
      )}
      <GridContainer spacing={3} className={classes.machineDetails}>
        <GridItem xs={6} className={classes.machineDetailTitle}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h2">Assigned Machine</Typography>
            {machineResyncData.length > 0 && <ResyncDetails resyncData={machineResyncData} onRefresh={refetch} />}
          </Box>
        </GridItem>
        <GridItem xs={6} className={classes.assignNewBtn}>
          <Button
            variant="outlined"
            onClick={assignMachineHandler}
            size="large"
            startIcon={<Add />}
            data-testid="assign-new-button"
          >
            ASSIGN NEW
          </Button>
        </GridItem>
      </GridContainer>
      {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
      {!isLoading && !isFetching && !isError && data && (
        <ReactTable
          data={data}
          columns={columns}
          stickyHeader
          tableHeight={300}
          expandable
          renderRowSubComponent={machineComponent}
        />
      )}
    </Box>
  );
};

export default AssetDetails;
